/**
 * Block 5.1 (2026-05-26): Month-Close Runner pages.
 *
 * List + minimal detail view. Editor (new run wizard) — TECHDEBT.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  monthCloseRuns,
  type MonthCloseRun,
  type MonthCloseRunListItem,
} from '@/api/month_close_runs';
import { orgs } from '@/api/catalogs';
import ClosingSnapshotCard from '@/components/dashboard/ClosingSnapshotCard';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Очікує',
  IN_PROGRESS: 'Виконується',
  COMPLETED: 'Завершено',
  PARTIAL: 'Частково',
  FAILED: 'Помилка',
  CANCELLED: 'Скасовано',
};

/** Людиночитабельний підсумок операції закриття (замість сирого JSON). */
function formatOpSummary(opType: string, summary: Record<string, unknown>): string {
  if (opType === 'PRODUCTION_COST_CLOSING') {
    const groups = Array.isArray(summary.groups) ? (summary.groups as Record<string, unknown>[]) : [];
    const variance = Number(summary.total_variance ?? 0);
    const entries = Number(summary.entries_count ?? 0);
    const remaining = groups.reduce((s, g) => s + Number(g.wip_remaining ?? 0), 0);
    const parts = [
      `варіація ${variance.toFixed(2)} грн`,
      `продукт-рядків ${entries}`,
      `груп ${groups.length}`,
    ];
    if (remaining > 0) parts.push(`незавершеного залишилось ${remaining.toFixed(2)}`);
    return parts.join(' · ');
  }
  return JSON.stringify(summary);
}


export function MonthCloseRunsPage() {
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState<string>('');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState<MonthCloseRun | null>(null);

  const orgList = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const list = useQuery({
    queryKey: ['month-close-runs', orgId],
    queryFn: () =>
      monthCloseRuns.list({
        organization_id: orgId || undefined,
        limit: 200,
      }),
  });

  const preCheckMut = useMutation({
    mutationFn: () =>
      monthCloseRuns.preCheck({
        organization_id: orgId,
        period_year: year,
        period_month: month,
      }),
  });

  const runMut = useMutation({
    mutationFn: () =>
      monthCloseRuns.run({
        organization_id: orgId,
        period_year: year,
        period_month: month,
      }),
    onMutate: () => setRunning(true),
    onSuccess: (data) => {
      setLastResult(data);
      setRunning(false);
      qc.invalidateQueries({ queryKey: ['month-close-runs'] });
    },
    onError: () => setRunning(false),
  });

  const cols = useMemo<ColumnDef<MonthCloseRunListItem>[]>(
    () => [
      {
        key: 'document_number', header: '№', width: 130, importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'period_year', header: 'Рік', width: 80, align: 'right',
        sortValue: (r) => r.period_year,
        cell: (r) => r.period_year,
      },
      {
        key: 'period_month', header: 'Місяць', width: 90, align: 'right',
        sortValue: (r) => r.period_month,
        cell: (r) => r.period_month,
      },
      {
        key: 'status', header: 'Статус', width: 130,
        sortValue: (r) => STATUS_LABELS[r.status] ?? r.status,
        cell: (r) => STATUS_LABELS[r.status] ?? r.status,
      },
      {
        key: 'pre_check_passed', header: 'Pre-check', width: 110,
        sortValue: (r) => (r.pre_check_passed ? 1 : 0),
        cell: (r) => (r.pre_check_passed ? '✓ пройдено' : '× блок'),
      },
      {
        key: 'started_at', header: 'Початок', width: 160,
        sortValue: (r) => r.started_at ?? '',
        cell: (r) =>
          r.started_at ? new Date(r.started_at).toLocaleString('uk-UA') : '',
      },
      {
        key: 'completed_at', header: 'Завершено', width: 160,
        sortValue: (r) => r.completed_at ?? '',
        cell: (r) =>
          r.completed_at ? new Date(r.completed_at).toLocaleString('uk-UA') : '',
      },
    ],
    [],
  );

  const canRun = orgId && year && month && !running;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Закриття місяців</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '8px 0', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          <option value="">— Оберіть юрособу —</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <input
          type="number" value={year} min={2020} max={2100}
          style={{ width: 80 }}
          onChange={(e) => setYear(Number(e.target.value))}
        />
        <input
          type="number" value={month} min={1} max={12}
          style={{ width: 60 }}
          onChange={(e) => setMonth(Number(e.target.value))}
        />
        <button
          disabled={!canRun}
          onClick={() => preCheckMut.mutate()}
        >
          Перевірити
        </button>
        <button
          disabled={!canRun}
          onClick={() => runMut.mutate()}
          style={{ background: '#080', color: 'white' }}
        >
          {running ? 'Виконання…' : 'Запустити закриття'}
        </button>
      </div>

      {preCheckMut.data && (
        <div style={{
          padding: 12, margin: '8px 0',
          background: preCheckMut.data.passed ? '#e6f9e6' : '#fee',
          border: '1px solid ' + (preCheckMut.data.passed ? '#080' : '#c00'),
        }}>
          <strong>
            {preCheckMut.data.passed
              ? '✓ Pre-check пройдено — можна запускати'
              : '× Pre-check заблоковано'}
          </strong>
          {preCheckMut.data.warnings.map((w, i) => (
            <div key={i} style={{ marginTop: 6 }}>
              ⚠ {w.warning_type}: {w.count} {' '}
              ({w.items.slice(0, 3).map((it: Record<string, unknown>) =>
                String(it.document_number ?? it.order_id ?? it.sick_leave_id ?? '?')
              ).join(', ')}
              {w.items.length > 3 ? '...' : ''})
            </div>
          ))}
        </div>
      )}

      {lastResult && (
        <>
          <div style={{
            padding: 12, margin: '8px 0',
            background: '#f0f6ff', border: '1px solid #06c',
          }}>
            <strong>Результат: {lastResult.document_number} (status={lastResult.status})</strong>
            {lastResult.operations_results.map((op, i) => (
              <div key={i} style={{ marginTop: 4 }}>
                {op.status === 'SUCCESS' ? '✓' : op.status === 'SKIPPED' ? '○' : '×'}{' '}
                {op.operation_type}: {op.status}
                {op.error_message ? ` — ${op.error_message}` : ''}
                {op.summary && Object.keys(op.summary).length > 0 ? (
                  <span style={{ color: '#666' }}> ({formatOpSummary(op.operation_type, op.summary)})</span>
                ) : null}
              </div>
            ))}
          </div>
          <ClosingSnapshotCard snapshot={lastResult.closing_snapshot} />
        </>
      )}

      <DataTable<MonthCloseRunListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_number"
        initialSortDir="desc"
      />
    </>
  );
}
